import React, { useState } from 'react';
import {
  Mail, Send, Loader2, CheckCircle2, MapPin, Building2,
  Clock, ShieldCheck, ArrowRight,
} from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { toast } from 'sonner';
import SEOHead from '@/components/marketing/SEOHead';
import PageHeader from '@/components/marketing/PageHeader';
import CTASection from '@/components/marketing/CTASection';
import DecorBackground from '@/components/marketing/DecorBackground';
import WorldMap from '@/components/marketing/WorldMap';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { breadcrumbJsonLd, localBusinessJsonLd, graph } from '@/lib/jsonld';

/* -------------------------------------------------------------
   Animation primitives — tuned to match Hero.js timing/feel.
   ------------------------------------------------------------- */
const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
};

export default function ContactPage() {
  const reduced = useReducedMotion();
  const [form, setForm] = useState({
    name: '', email: '', company: '', message: '', companyWebsiteHp: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Valid email required';
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = 'Please add a few details (10+ chars)';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onSubmit(ev) {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post('/contact', form);
      setSent(true);
      toast.success('Message sent. We’ll be in touch.');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to send message.';
      toast.error(typeof msg === 'string' ? msg : 'Failed to send message.');
    } finally {
      setSubmitting(false);
    }
  }

  // Use reduced animation variants when user prefers reduced motion.
  const stg = reduced ? {} : stagger;
  const up = reduced ? {} : fadeUp;
  const motionInitial = reduced ? false : 'hidden';

  return (
    <>
      <SEOHead
        path="/contact"
        jsonLd={graph(
          breadcrumbJsonLd([
            { label: 'Home', path: '/' },
            { label: 'Contact' },
          ]),
          localBusinessJsonLd(),
        )}
      />
      <PageHeader
        breadcrumbs={[{ label: 'Contact' }]}
        eyebrow="Talk to us"
        title="Reach the Ads Dashbound team"
        subtitle="For advertiser onboarding, please use the application form. For everything else — partnerships, press, general questions — contact us below."
      />

      {/* Main dark contact section — matches homepage visual identity */}
      <section
        className="relative bg-[#050B17] text-white isolate overflow-hidden"
        data-testid="contact-section"
      >
        <DecorBackground variant="section" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <motion.div
            initial={motionInitial}
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            variants={stg}
            className="grid lg:grid-cols-12 gap-8 lg:gap-10"
          >
            {/* ---------- Left column: contact info & trust panels ---------- */}
            <aside className="lg:col-span-5 space-y-5">
              {/* World-map cyber panel: status + GEO availability */}
              <motion.div
                variants={up}
                className="
                  relative rounded-2xl overflow-hidden h-[260px]
                  bg-gradient-to-b from-white/[0.06] to-white/[0.02]
                  border border-[#7DDCFF]/20
                  backdrop-blur-xl
                  shadow-[0_24px_60px_rgba(0,0,0,0.45)]
                "
                data-testid="contact-world-card"
              >
                <div
                  aria-hidden="true"
                  className="absolute inset-0 opacity-[0.32] pointer-events-none"
                >
                  <WorldMap intensity="medium" showLabels={false} />
                </div>
                {/* Top edge gradient */}
                <div
                  aria-hidden="true"
                  className="absolute top-0 left-0 right-0 h-px"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent, rgba(34,211,238,0.65), rgba(124,58,237,0.65), transparent)',
                  }}
                />
                <div className="absolute inset-0 z-10 p-6 flex flex-col justify-between">
                  <div>
                    <span
                      className="
                        inline-flex items-center gap-2
                        px-3 py-1 rounded-full
                        bg-white/[0.04] border border-white/10
                        text-[11px] font-semibold uppercase tracking-[0.18em] text-[#22D3EE]
                        backdrop-blur-md
                      "
                    >
                      <span className="relative inline-flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#22D3EE] opacity-75 pulse-glow" />
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#22D3EE]" />
                      </span>
                      Online — typical reply &lt; 24h
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <PingChip color="#22D3EE" label="US / EU" />
                    <PingChip color="#34D399" label="LATAM" />
                    <PingChip color="#8B5CF6" label="APAC" />
                    <PingChip color="#FBBF24" label="MENA" />
                  </div>
                </div>
              </motion.div>

              {/* Email + Telegram glass cards */}
              <div className="grid sm:grid-cols-2 gap-4">
                <motion.div variants={up}>
                  <ContactGlassCard
                    href="mailto:contact@dashbound.media"
                    icon={Mail}
                    accent="#22D3EE"
                    label="Email"
                    value="contact@dashbound.media"
                    testId="contact-email-card"
                  />
                </motion.div>
                <motion.div variants={up}>
                  <ContactGlassCard
                    href="https://t.me/dashbound_media"
                    external
                    icon={Send}
                    accent="#8B5CF6"
                    label="Telegram"
                    value="@dashbound_media"
                    testId="contact-telegram-card"
                  />
                </motion.div>
              </div>

              {/* Company card */}
              <motion.div
                variants={up}
                className="
                  rounded-2xl
                  bg-gradient-to-b from-white/[0.05] to-white/[0.02]
                  border border-white/10
                  backdrop-blur-xl
                  p-5
                  lift-on-hover
                "
                data-testid="contact-company-card"
              >
                <div className="flex items-start gap-3">
                  <span
                    className="
                      inline-flex h-10 w-10 items-center justify-center rounded-xl
                      bg-gradient-to-br from-[#22D3EE]/15 to-[#7C3AED]/15
                      border border-white/10
                      text-[#A78BFA]
                      shadow-[0_0_20px_rgba(124,58,237,0.25)]
                      shrink-0
                    "
                  >
                    <Building2 className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Company
                    </span>
                    <p className="mt-1 font-display text-[15px] font-semibold text-white">
                      DASHBOUND MEDIA - FZCO
                    </p>
                    <p className="mt-0.5 text-[12.5px] text-slate-400/90">
                      Operating Ads Dashbound on Dashbound.media
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Office address card */}
              <motion.div
                variants={up}
                className="
                  rounded-2xl
                  bg-gradient-to-b from-white/[0.05] to-white/[0.02]
                  border border-white/10
                  backdrop-blur-xl
                  p-5
                  lift-on-hover
                "
                data-testid="contact-address-card"
              >
                <div className="flex items-start gap-3">
                  <span
                    className="
                      inline-flex h-10 w-10 items-center justify-center rounded-xl
                      bg-[#34D399]/15
                      border border-[#34D399]/30
                      text-[#34D399]
                      shadow-[0_0_20px_rgba(52,211,153,0.25)]
                      shrink-0
                    "
                  >
                    <MapPin className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Office address
                    </span>
                    <address className="mt-1 not-italic text-[13.5px] text-slate-200/90 leading-relaxed">
                      Building A1, Dubai Digital Park<br />
                      Dubai Silicon Oasis<br />
                      Dubai, United Arab Emirates
                    </address>
                    <a
                      href="https://www.google.com/maps/search/?api=1&query=Building+A1+Dubai+Digital+Park+Dubai+Silicon+Oasis+Dubai+United+Arab+Emirates"
                      target="_blank"
                      rel="noreferrer noopener"
                      data-testid="contact-address-map-link"
                      className="
                        mt-3 inline-flex items-center gap-1
                        text-[12px] font-semibold text-[#22D3EE]
                        hover:text-white transition-colors
                      "
                    >
                      View on map <ArrowRight className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              </motion.div>

              {/* Privacy + response strip */}
              <motion.div
                variants={up}
                className="
                  rounded-2xl
                  bg-[#0B172A]/85
                  border border-white/10
                  p-5
                  flex items-start gap-3
                "
                data-testid="contact-privacy-strip"
              >
                <span
                  className="
                    inline-flex h-9 w-9 items-center justify-center rounded-xl
                    bg-[#22D3EE]/10 border border-[#22D3EE]/30 text-[#22D3EE] shrink-0
                  "
                >
                  <ShieldCheck className="h-4 w-4" />
                </span>
                <p className="text-[12.5px] text-slate-300/90 leading-relaxed">
                  <strong className="text-white font-semibold">Privacy:</strong>{' '}
                  Messages are read only by the Ads Dashbound team. We don’t
                  share contact data with third parties.
                </p>
              </motion.div>
            </aside>

            {/* ---------- Right column: dark glass form / success card ---------- */}
            <div className="lg:col-span-7">
              {sent ? (
                <motion.div
                  variants={up}
                  initial={motionInitial}
                  animate="show"
                  className="
                    relative rounded-2xl overflow-hidden
                    bg-gradient-to-b from-white/[0.05] to-white/[0.02]
                    border border-white/10
                    backdrop-blur-xl
                    p-8 sm:p-10
                    shadow-[0_30px_80px_rgba(0,0,0,0.45)]
                  "
                  data-testid="contact-success-card"
                >
                  <div
                    aria-hidden="true"
                    className="absolute top-0 left-0 right-0 h-px"
                    style={{
                      background:
                        'linear-gradient(90deg, transparent, rgba(52,211,153,0.65), rgba(34,211,238,0.65), transparent)',
                    }}
                  />
                  <div className="flex items-center gap-3">
                    <span
                      className="
                        inline-flex h-12 w-12 items-center justify-center rounded-xl
                        bg-[#34D399]/15 border border-[#34D399]/30 text-[#34D399]
                        shadow-[0_0_28px_rgba(52,211,153,0.35)]
                      "
                    >
                      <CheckCircle2 className="h-6 w-6" />
                    </span>
                    <h2 className="font-display text-[22px] sm:text-[26px] font-bold text-white tracking-[-0.02em]">
                      Message received
                    </h2>
                  </div>
                  <p className="mt-4 text-[14.5px] text-slate-300/90 leading-relaxed">
                    Thanks for reaching out. A team member will reply by email
                    within 1–2 business days. If your message is urgent,
                    Telegram is usually fastest.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Button
                      asChild
                      className="bg-white/[0.04] text-white border border-white/15 hover:bg-white/10 hover:text-white rounded-xl backdrop-blur-md transition-colors"
                      variant="outline"
                    >
                      <a
                        href="https://t.me/dashbound_media"
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-2"
                      >
                        <Send className="h-4 w-4" /> Continue on Telegram
                      </a>
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <motion.form
                  variants={up}
                  onSubmit={onSubmit}
                  data-testid="contact-form"
                  noValidate
                  className="
                    relative rounded-2xl overflow-hidden
                    bg-gradient-to-b from-white/[0.05] to-white/[0.02]
                    border border-white/10
                    backdrop-blur-xl
                    p-6 sm:p-8
                    shadow-[0_30px_80px_rgba(0,0,0,0.45)]
                    space-y-7
                  "
                >
                  {/* Top edge gradient */}
                  <div
                    aria-hidden="true"
                    className="absolute top-0 left-0 right-0 h-px"
                    style={{
                      background:
                        'linear-gradient(90deg, transparent, rgba(34,211,238,0.55), rgba(124,58,237,0.55), transparent)',
                    }}
                  />
                  {/* Soft corner glow */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-16 -right-16 h-56 w-56 rounded-full"
                    style={{
                      background:
                        'radial-gradient(circle, rgba(34,211,238,0.18), transparent 70%)',
                    }}
                  />

                  {/* Honeypot */}
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    name="company-website"
                    data-testid="contact-honeypot-field"
                    value={form.companyWebsiteHp}
                    onChange={(e) => set('companyWebsiteHp', e.target.value)}
                    style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }}
                  />

                  <SectionHeading number="01" title="Your details">
                    <Field label="Name *" error={errors.name}>
                      <DarkInput
                        data-testid="contact-name-input"
                        value={form.name}
                        onChange={(e) => set('name', e.target.value)}
                        placeholder="Your full name"
                      />
                    </Field>
                    <Field label="Work email *" error={errors.email}>
                      <DarkInput
                        type="email"
                        data-testid="contact-email-input"
                        value={form.email}
                        onChange={(e) => set('email', e.target.value)}
                        placeholder="you@company.com"
                      />
                    </Field>
                    <Field label="Company" full>
                      <DarkInput
                        data-testid="contact-company-input"
                        value={form.company}
                        onChange={(e) => set('company', e.target.value)}
                        placeholder="Your company"
                      />
                    </Field>
                  </SectionHeading>

                  <SectionHeading number="02" title="How can we help?">
                    <Field label="Message *" error={errors.message} full>
                      <DarkTextarea
                        rows={6}
                        data-testid="contact-message-textarea"
                        value={form.message}
                        onChange={(e) => set('message', e.target.value)}
                        placeholder="Tell us how we can help — partnerships, press, advertiser questions, technical questions, anything else."
                      />
                    </Field>
                  </SectionHeading>

                  <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                    <p className="text-[12px] text-slate-400/90 inline-flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5 text-[#22D3EE]" />
                      Most messages are answered within 1–2 business days.
                    </p>
                    <Button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit-button"
                      className="
                        btn-primary-gradient text-white border-0
                        rounded-xl px-6 py-6 text-[14.5px] font-semibold
                        shadow-[0_18px_45px_rgba(34,211,238,0.25)]
                        w-full sm:w-auto
                      "
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…
                        </>
                      ) : (
                        <span className="inline-flex items-center gap-2">
                          Send message <ArrowRight className="h-4 w-4" />
                        </span>
                      )}
                    </Button>
                  </div>
                </motion.form>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <CTASection
        eyebrow="Want to launch?"
        title="Skip the line — apply as advertiser."
        subtitle="If you’re ready to launch, the advertiser application is the fastest path to a real recommendation and approval."
      />
    </>
  );
}

/* -------------------------------------------------------------
   Sub-components — local to this page
   ------------------------------------------------------------- */

function ContactGlassCard({ icon: Icon, label, value, href, external, accent, testId }) {
  const isExternal = !!external;
  return (
    <a
      href={href}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noreferrer noopener' : undefined}
      data-testid={testId}
      className="
        group relative block h-full
        rounded-2xl
        bg-gradient-to-b from-white/[0.05] to-white/[0.02]
        border border-white/10
        backdrop-blur-xl
        p-5
        transition-colors duration-200
        hover:border-[#7DDCFF]/30
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#22D3EE]/60
      "
    >
      <span
        className="inline-flex h-10 w-10 items-center justify-center rounded-xl border"
        style={{
          background: `linear-gradient(180deg, ${accent}26, ${accent}10)`,
          borderColor: `${accent}55`,
          color: accent,
          boxShadow: `0 0 18px ${accent}33`,
        }}
        aria-hidden="true"
      >
        <Icon className="h-5 w-5" />
      </span>
      <span className="mt-4 block text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-400">
        {label}
      </span>
      <span
        className="
          mt-1 block font-display text-[14.5px] font-semibold text-white
          group-hover:text-[#7DDCFF] transition-colors
        "
      >
        {value}
      </span>
    </a>
  );
}

function PingChip({ color, label }) {
  return (
    <span
      className="
        inline-flex items-center gap-2
        px-2.5 py-1 rounded-full
        bg-[#0B172A]/85 border border-white/10
        text-[11px] font-medium text-slate-200
        backdrop-blur-md
      "
    >
      <span className="relative inline-flex h-1.5 w-1.5">
        <span
          className="absolute inline-flex h-full w-full rounded-full opacity-70 pulse-glow"
          style={{ background: color }}
        />
        <span
          className="relative inline-flex h-1.5 w-1.5 rounded-full"
          style={{ background: color }}
        />
      </span>
      {label}
    </span>
  );
}

function SectionHeading({ number, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <span className="font-display text-[12.5px] font-mono-tabular text-[#22D3EE]">{number}</span>
        <span className="h-px flex-1 bg-white/10" />
        <h2 className="font-display text-[12.5px] font-semibold tracking-[0.06em] text-white uppercase">
          {title}
        </h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, error, full, children }) {
  return (
    <div className={full ? 'sm:col-span-2 space-y-1.5' : 'space-y-1.5'}>
      <Label className="text-[12px] font-medium text-slate-300 uppercase tracking-[0.06em]">
        {label}
      </Label>
      {children}
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}

function DarkInput(props) {
  return (
    <Input
      {...props}
      className="
        bg-[#0B172A] border-white/10 text-white
        placeholder:text-slate-500
        focus-visible:ring-[#22D3EE]/50
        h-11
      "
    />
  );
}

function DarkTextarea(props) {
  return (
    <Textarea
      {...props}
      className="
        bg-[#0B172A] border-white/10 text-white
        placeholder:text-slate-500
        focus-visible:ring-[#22D3EE]/50
      "
    />
  );
}
